"use client";

import { useMountEffect } from "@/ui";
import { run as SetupCookieConsent } from "vanilla-cookieconsent";

import { updateGoogleConsent } from "@/providers/gooogle";
import { updateIntercomConsent } from "@/providers/intercom";
import { updateAppsFlyerConsent } from "@/providers/onelink";

import "vanilla-cookieconsent/dist/cookieconsent.css";


type Category = (
  | "necessary"
  | "functionality"
  | "analytics"
  | "marketing"
);


/** allows to use the cookie banner in a server component */
export function CookieBanner() {
  useCookieConsent();
  return null;
}


export function useCookieConsent() {
  useMountEffect(() => {
    SetupCookieConsent({
      autoShow: true,
      mode: "opt-in",
      revision: 1,
      disablePageInteraction: false,
      hideFromBots: true,

      cookie: {
        name: "cc_cookie",
      },

      /** fired on consent, and every time after that */
      onConsent: ({ cookie }) => {
        const categories = cookie.categories as Category[];
        const functionalityGranted = categories.includes("functionality");
        const analyticsGranted = categories.includes("analytics");
        const marketingGranted = categories.includes("marketing");

        updateGoogleConsent(analyticsGranted);
        updateIntercomConsent(functionalityGranted);
        updateAppsFlyerConsent(marketingGranted);
      },

      //onChange: ({ changedCategories, changedServices }) => {
        //console.log("onChange fired!", changedCategories, changedServices);
      //},

      guiOptions: {
        consentModal: {
          layout: "box wide",
          position: "bottom left",
          equalWeightButtons: false,
          flipButtons: true,
        },
        preferencesModal: {
          layout: "box",
          position: "right",
          equalWeightButtons: true,
          flipButtons: false,
        }
      },

      categories: {
        necessary: {
          readOnly: true,
        },
        /** I.e. Google Tag Manager */
        analytics: {
          autoClear: {
            cookies: [
              { name: /^_ga/ },
              { name: "_gid" },
            ],
          },
          services: {
            ga: {
              label: "Google Analytics",
            },
          },
        },
        /** I.e. Intercom */
        functionality: {
          autoClear: {
            cookies: [
              { name: "intercom-id-*" },
              { name: "intercom-session-*" },
            ],
          },
          services: {
            intercom: {
              label: "Intercom Chat",
            },
          },
        },
        /** I.e. AppsFlyer OneLink */
        marketing: {
          autoClear: {
            cookies: [
              { name: "_af*" },
              { name: "af*" },
            ],
          },
          services: {
            appsflyer: {
              label: "AppsFlyer OneLink",
            },
          },
        },
      },

      language: {
        default: "en",
        //autoDetect: "browser",
        autoDetect: "document",
        translations: {

          en: {
            consentModal: {
              title: "We use cookies",
              description: `
                We use cookies to enhance your user experience, gather general visitor
                information, and track visits to our website.
              `,
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Manage individual preferences",
              footer: `
                <a target="_bank" href="/en/privacy-policy">Privacy Policy</a>
                <a target="_bank" href="/en/terms-and-conditions">Terms of Use</a>
              `,
            },
            preferencesModal: {
              title: "Manage cookie preferences",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save preferences",
              closeIconLabel: "Close modal",
              serviceCounterLabel: "Service|Services",
              sections: [
                {
                  title: "Your Privacy Choices",
                  description: `
                    In this panel you can express some preferences related to the
                    processing of your personal information. You may review and change
                    expressed choices at any time by resurfacing this panel via the
                    provided link. To deny your consent to the specific processing
                    activities described below, switch the toggles to off or use the
                    “Reject all” button and confirm you want to save your choices.
                  `,
                },
                {
                  title: "Strictly Necessary",
                  description: `
                    These cookies are essential to provide you with services available
                    through our website and to enable you to use some of its features.
                    They help to authenticate users and prevent fraudulent use of user
                    accounts. Without these cookies, the services that you have asked for
                    cannot be provided, and we only use these cookies to provide you with
                    those services.
                  `,

                  /** this field will generate a toggle linked to the "necessary" category */
                  linkedCategory: "necessary",
                },
                {
                  title: "Functionality Cookies",
                  description: `
                    We use functionality cookies to provide enhanced features and customization
                    on our website. These cookies allow us to remember your preferences and
                    enable tools like live chat or content recommendations. Without these cookies,
                    some features may not work properly.
                  `,
                  linkedCategory: "functionality",
                },
                {
                  title: "Performance and Analytics",
                  description: `
                    We use analytics cookies to track information about how the Website
                    is used so we can make improvements. We may also use analytics cookies
                    to test new advertisements, pages, features, or new functionality of
                    the Website to see how our users react to them. Specifically, we use
                    Google Analytics cookies to help measure how users interact with our
                    website content.
                  `,
                  linkedCategory: "analytics",
                },
                {
                  title: "Marketing and Attribution",
                  description: `
                    We use marketing and attribution cookies to track the effectiveness of our
                    marketing campaigns and referral links. These cookies help us understand how
                    users arrive on our site and interact with our content, enabling us to optimize
                    offers and promotions. Specifically, we use AppsFlyer to generate smart links
                    and track app downloads.
                  `,
                  linkedCategory: "marketing",
                }
              ],
            },
          },

          "fr": {
            consentModal: {
              title: "Nous utilisons des cookies",
              description: `
                Nous utilisons des cookies pour améliorer votre expérience utilisateur,
                collecter des informations générales sur les visiteurs et suivre les
                visites sur notre site.
              `,
              acceptAllBtn: "Tout accepter",
              acceptNecessaryBtn: "Tout refuser",
              showPreferencesBtn: "Gérer les préférences individuelles",
              footer: `
                <a target="_bank" href="/fr/politique-de-confidentialite">Politique de confidentialité</a>
                <a target="_bank" href="/fr/conditions-utilisation">Conditions génerales d'utilisation</a>
              `,
            },
            preferencesModal: {
              title: "Gérer les préférences de cookies",
              acceptAllBtn: "Tout accepter",
              acceptNecessaryBtn: "Tout refuser",
              savePreferencesBtn: "Enregistrer les préférences",
              closeIconLabel: "Fermer la fenêtre",
              serviceCounterLabel: "Service|Services",
              sections: [
                {
                  title: "Vos choix en matière de confidentialité",
                  description: `
                    Dans ce panneau, vous pouvez exprimer certaines préférences liées au
                    traitement de vos informations personnelles. Vous pouvez consulter et
                    modifier vos choix à tout moment en réaffichant ce panneau via le lien
                    prévu. Pour refuser votre consentement aux activités de traitement
                    spécifiques décrites ci-dessous, désactivez les boutons ou utilisez le
                    bouton “Tout refuser” et confirmez l’enregistrement de vos choix.
                  `,
                },
                {
                  title: "Strictement nécessaire",
                  description: `
                    Ces cookies sont essentiels pour vous fournir les services disponibles
                    sur notre site et vous permettre d’utiliser certaines de ses
                    fonctionnalités. Ils aident à authentifier les utilisateurs et à
                    prévenir l’utilisation frauduleuse des comptes. Sans ces cookies, les
                    services que vous avez demandés ne peuvent pas être fournis, et nous
                    les utilisons uniquement pour vous fournir ces services.
                  `,
                  linkedCategory: "necessary",
                },
                {
                  title: "Cookies fonctionnels",
                  description: `
                    Nous utilisons des cookies fonctionnels pour offrir des fonctionnalités avancées
                    et des options de personnalisation sur notre site. Ces cookies nous permettent
                    de mémoriser vos préférences et d’activer des outils comme le chat en direct
                    ou les recommandations de contenu. Sans ces cookies, certaines fonctionnalités
                    pourraient ne pas fonctionner correctement.
                  `,
                  linkedCategory: "functionality",
                },
                {
                  title: "Performance et analyses",
                  description: `
                    Nous utilisons des cookies d’analyse pour suivre les informations sur
                    l’utilisation du site afin de pouvoir l’améliorer. Nous pouvons
                    également utiliser ces cookies pour tester de nouvelles publicités,
                    pages, fonctionnalités ou autres éléments afin de voir comment les
                    utilisateurs y réagissent. Plus précisément, nous utilisons les cookies
                    Google Analytics pour mesurer l’interaction des utilisateurs avec le
                    contenu de notre site.
                  `,
                  linkedCategory: "analytics",
                },
                {
                  title: "Marketing et attribution",
                  description: `
                    Nous utilisons des cookies marketing et d’attribution pour suivre l’efficacité
                    de nos campagnes marketing et de nos liens de parrainage. Ces cookies nous aident
                    à comprendre comment les utilisateurs arrivent sur notre site et interagissent
                    avec notre contenu, afin d’optimiser nos offres et promotions. Plus précisément,
                    nous utilisons AppsFlyer pour générer des smart links et suivre les téléchargements
                    de l’application.
                  `,
                  linkedCategory: "marketing",
                }
              ],
            },
            /* END FR */

          },
        },
      },
    });
  });
}
